import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import {
  OrganizationInvitation,
  OrganizationInvitationStatus,
} from 'types/invitation';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useHasAccess } from 'hooks/useHasAccess';

export type UseOrganizationInvitationsParams = {
  emails?: string[];
  invitation_ids?: string[];
  inviter_ids?: string[];
  statuses?: OrganizationInvitationStatus[];
};

type OrganizationInvitationsOptions = UseQueryOptions<
  OffsetPaginated<OrganizationInvitation>,
  APIError
> & {
  params?: UseOrganizationInvitationsParams & OffsetPaginationParams;
};

/** List invitations in an organization */
export function useOrganizationInvitations({
  queryKey = ['organization-invitations'],
  params,
  enabled,
  ...options
}: OrganizationInvitationsOptions = {}) {
  const { applicationID, client } = useAPI();
  const { data: organization } = useCurrentOrganization();

  const organizationId = organization?.id;

  const [hasAccess] = useHasAccess();
  const queryParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      if (!organizationId) {
        return Promise.reject(new Error('No organization id provided.'));
      }

      const { data } = await client.get<
        OffsetPaginated<OrganizationInvitation>
      >(`/internal/orgs/${organizationId}/invitations`, {
        params: queryParams,
      });
      return data;
    },
    {
      enabled:
        enabled !== undefined
          ? enabled &&
            Boolean(organizationId) &&
            hasAccess('organization_settings')
          : Boolean(organizationId) && hasAccess('organization_settings'),
      ...options,
    }
  );
}
